import _Link from "@mui/material/Link";
import _CircularProgress from "@mui/material/CircularProgress";
import _Button from "@mui/material/Button";
import _CardActionArea from "@mui/material/CardActionArea";
import _CardContent from "@mui/material/CardContent";
import _AddPhotoAlternate from "@mui/icons-material/AddPhotoAlternate";
import _Card from "@mui/material/Card";
import _ButtonBase from "@mui/material/ButtonBase";
import _CardMedia from "@mui/material/CardMedia";
import _TextField from "@mui/material/TextField";
import _Divider from "@mui/material/Divider";
import _Stack from "@mui/material/Stack";
import _Typography from "@mui/material/Typography";
import _Box from "@mui/material/Box";
import React, { useEffect, useRef, useState } from 'react';
import { RemoveIcon } from './icons';
import { PanoramaxPreviewCard } from './panoramax-preview';
export function ParkingDetailsPanel({
  contribution,
  eyebrow,
  icon,
  loadPanoramaxPreview,
  onAction,
  osmLabel,
  osmUrl,
  rows,
  title
}) {
  const [contributionOpen, openContribution] = useState(false);
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState(false);
  const [panoramaxPreview, setPanoramaxPreview] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const photosInputRef = useRef(null);
  const photosRef = useRef([]);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  useEffect(() => {
    return () => {
      photosRef.current.forEach(({
        preview
      }) => URL.revokeObjectURL(preview));
    };
  }, []);
  useEffect(() => {
    let active = true;
    setPanoramaxPreview(null);
    if (loadPanoramaxPreview) {
      loadPanoramaxPreview().then(preview => {
        if (active) setPanoramaxPreview(preview);
      });
    }
    return () => {
      active = false;
    };
  }, [loadPanoramaxPreview]);
  function clearPhotos() {
    setPhotos(prevPhotos => {
      prevPhotos.forEach(({
        preview
      }) => URL.revokeObjectURL(preview));
      return [];
    });
  }
  function handlePhotosChange({
    target: {
      files
    }
  }) {
    if (!files || files.length === 0) return;
    const time = new Date().getTime();
    const addedPhotos = Array.from(files).map((file, index) => ({
      file,
      id: `${time}-${index}`,
      preview: URL.createObjectURL(file)
    }));
    setPhotos(prevPhotos => [...prevPhotos, ...addedPhotos]);
  }
  function removePhoto(id) {
    setPhotos(prevPhotos => prevPhotos.filter(photo => {
      if (photo.id !== id) return true;
      URL.revokeObjectURL(photo.preview);
      return false;
    }));
  }
  function closeContribution() {
    setDescription('');
    setDescriptionError(false);
    clearPhotos();
    openContribution(false);
  }
  async function handleSubmit(event) {
    event.preventDefault();
    if (!contribution) return;
    const trimmedDescription = description.trim();
    if (!trimmedDescription) {
      setDescriptionError(true);
      return;
    }
    setDescriptionError(false);
    setSubmitting(true);
    try {
      await contribution.onSubmit(trimmedDescription, photos.map(({
        file
      }) => file));
      closeContribution();
    } finally {
      setSubmitting(false);
    }
  }
  return /*#__PURE__*/React.createElement(_Box, {
    onClick: event => event.stopPropagation(),
    onMouseDown: event => event.stopPropagation()
  }, /*#__PURE__*/React.createElement(_Stack, {
    spacing: 2
  }, /*#__PURE__*/React.createElement(_Stack, {
    alignItems: "flex-start",
    direction: "row",
    spacing: 1.5
  }, icon && /*#__PURE__*/React.createElement(_Box, {
    alt: "",
    component: "img",
    src: icon,
    sx: {
      bgcolor: 'grey.800',
      borderRadius: 1,
      height: 32,
      objectFit: 'none',
      p: 0.75,
      width: 32
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    minWidth: 0
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    component: "p",
    variant: "caption"
  }, eyebrow), /*#__PURE__*/React.createElement(_Typography, {
    component: "h3",
    fontWeight: 700,
    lineHeight: 1.2,
    variant: "subtitle1"
  }, title))), /*#__PURE__*/React.createElement(_Stack, {
    divider: /*#__PURE__*/React.createElement(_Divider, null),
    spacing: 0
  }, rows.map(({
    label,
    value
  }) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-start",
    display: "grid",
    gap: 1.5,
    gridTemplateColumns: "minmax(92px, 0.9fr) minmax(0, 1.1fr)",
    key: label,
    py: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    variant: "body2"
  }, label.replace(/ :$/, '').replace(/:$/, '')), /*#__PURE__*/React.createElement(_Typography, {
    align: "right",
    color: "text.primary",
    component: "div",
    fontWeight: 600,
    sx: {
      overflowWrap: 'anywhere'
    },
    variant: "body2"
  }, value)))), loadPanoramaxPreview && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Divider, null), /*#__PURE__*/React.createElement(_Box, null, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    fontWeight: 700,
    mb: 1,
    variant: "body2"
  }, "Panoramax"), panoramaxPreview && /*#__PURE__*/React.createElement(_Box, {
    onClickCapture: ({
      target
    }) => {
      if (target instanceof Element && target.closest('a')) onAction?.('panoramax_click');
    }
  }, /*#__PURE__*/React.createElement(PanoramaxPreviewCard, panoramaxPreview)))), (contribution || osmUrl) && /*#__PURE__*/React.createElement(_Divider, null), contributionOpen && contribution ? /*#__PURE__*/React.createElement(_Box, {
    component: "form",
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(_Stack, {
    spacing: 1.5
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    multiline: true,
    error: descriptionError,
    helperText: descriptionError ? contribution.descriptionRequiredError : undefined,
    label: contribution.descriptionLabel,
    minRows: 3,
    onChange: event => {
      setDescription(event.target.value);
      if (descriptionError) setDescriptionError(false);
    },
    size: "small",
    value: description
  }), /*#__PURE__*/React.createElement(_Box, null, /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    component: "p",
    mb: 0.75,
    variant: "caption"
  }, contribution.photosLabel), /*#__PURE__*/React.createElement(_Stack, {
    direction: "row",
    flexWrap: "wrap",
    gap: 1
  }, photos.map(({
    id,
    preview
  }) => /*#__PURE__*/React.createElement(_Card, {
    key: id,
    sx: {
      borderRadius: 1,
      position: 'relative'
    },
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(_CardMedia, {
    component: "img",
    height: 56,
    src: preview,
    sx: {
      width: 56
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "rgba(0, 0, 0, 0.5)",
    bottom: 0,
    component: _ButtonBase,
    disabled: submitting,
    display: "flex",
    justifyContent: "center",
    left: 0,
    onClick: () => removePhoto(id),
    position: "absolute",
    right: 0,
    sx: {
      color: '#fff',
      opacity: 0,
      '&:hover, &:focus-visible': {
        opacity: 1
      }
    },
    title: contribution.removePhotoLabel,
    top: 0,
    type: "button"
  }, /*#__PURE__*/React.createElement(RemoveIcon, {
    fontSize: "small"
  })))), /*#__PURE__*/React.createElement(_Card, {
    sx: {
      borderRadius: 1
    },
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(_CardActionArea, {
    disabled: submitting,
    onClick: () => photosInputRef.current?.click(),
    title: contribution.addPhotoLabel
  }, /*#__PURE__*/React.createElement(_CardContent, {
    sx: {
      alignItems: 'center',
      display: 'flex',
      height: 56,
      justifyContent: 'center',
      padding: 0,
      width: 56
    }
  }, /*#__PURE__*/React.createElement(_AddPhotoAlternate, {
    color: "action"
  }))))), /*#__PURE__*/React.createElement("input", {
    multiple: true,
    accept: "image/*",
    onChange: handlePhotosChange,
    onClick: ({
      currentTarget
    }) => currentTarget.value = '',
    ref: photosInputRef,
    style: {
      display: 'none'
    },
    type: "file"
  })), /*#__PURE__*/React.createElement(_Stack, {
    direction: "row",
    justifyContent: "flex-end",
    spacing: 1
  }, /*#__PURE__*/React.createElement(_Button, {
    disabled: submitting,
    onClick: () => {
      onAction?.('contribution_cancel');
      closeContribution();
    },
    size: "small",
    type: "button"
  }, contribution.cancelLabel), /*#__PURE__*/React.createElement(_Button, {
    disabled: submitting,
    size: "small",
    type: "submit",
    variant: "contained"
  }, submitting && /*#__PURE__*/React.createElement(_CircularProgress, {
    color: "inherit",
    size: 14,
    sx: {
      mr: 1
    }
  }), contribution.submitLabel)))) : /*#__PURE__*/React.createElement(_Stack, {
    alignItems: "center",
    spacing: 1
  }, contribution && /*#__PURE__*/React.createElement(_Button, {
    onClick: () => {
      onAction?.('contribution_open');
      openContribution(true);
    },
    size: "small",
    variant: "contained"
  }, contribution.buttonLabel), osmUrl && /*#__PURE__*/React.createElement(_Link, {
    href: osmUrl,
    onClick: () => onAction?.('osm_link_click'),
    rel: "noreferrer",
    target: "_blank",
    underline: "hover"
  }, osmLabel))));
}